import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Activity, Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Selamat datang, ${u.username}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] grid-noise flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#06B6D4]/10 rounded-full blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md relative z-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#06B6D4] flex items-center justify-center">
            <Activity className="text-white" size={22} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-white leading-none">PanelPulse</h1>
            <p className="text-xs text-[#9CA3AF] mt-1">Panel Monitoring Log</p>
          </div>
        </div>

        <div className="bg-[#111827]/80 backdrop-blur-xl border border-[#1F2937] rounded-2xl p-8 shadow-2xl">
          <h2 className="font-display text-xl font-bold text-white mb-1">Masuk ke dashboard</h2>
          <p className="text-sm text-[#9CA3AF] mb-6">Pantau log dan status perangkat secara real-time.</p>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-[#9CA3AF]">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder=""
                autoComplete="username"
                className="bg-[#0B0F17] border-[#1F2937] text-white h-11 focus-visible:ring-[#3B82F6]"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#9CA3AF]">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=""
                autoComplete="current-password"
                className="bg-[#0B0F17] border-[#1F2937] text-white h-11 focus-visible:ring-[#3B82F6]"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Masuk"}
            </Button>
          </form>
        </div>
        <div className="mt-5 flex items-center justify-center gap-2 text-xs text-[#6B7280]">
          <ShieldCheck size={14} /> JWT + bcrypt · akses per-PC ditegakkan di backend
        </div>
      </motion.div>
    </div>
  );
}
